import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { PageRoutes } from "../../../constants/routes";
import { appActions } from "../../../ducks/app";
import { Client, clientActions } from "../../../ducks/client";
import { useNavigation } from "../../../hooks/useNavigation";
import { AppDispatch } from "../../../store";
import { formatDateTime } from "../../../utils/date";
import { DetailItem } from "../../templates/DetailTemplate";

export const useClientDetail = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();
  const { clientId } = useParams<{ clientId: string }>();

  const [client, setClient] = useState<Client | null>(null);

  useEffect(() => {
    if (client || !clientId) return;

    dispatch(appActions.showLoading());

    dispatch(
      clientActions.getClientDetails.started({
        customerId: clientId,
        onComplete: () => dispatch(appActions.hideLoading()),
        onSuccess: (data) => {
          const clientData = data.data;
          const client: Client = {
            id: clientData.id,
            name: clientData.name,
            parentId: clientData.parent_id,
            validPeriod: {
              from: new Date(clientData.valid_from),
              to: new Date(clientData.valid_to),
            },
            createdAt: new Date(clientData.created_at),
            createdBy: clientData.created_by,
          };

          setClient(client);
        },
        onError: () => {
          navigateToPage(PageRoutes.CLIENTS);
        },
      }),
    );
  }, []);

  const clientDetails: DetailItem[] = useMemo(() => {
    if (!client) return [];

    return [
      {
        name: "顧客ID",
        value: client.id,
      },
      {
        name: "親顧客ID",
        value: client.parentId ?? "",
      },
      {
        name: "顧客名",
        value: client.name,
      },
      {
        name: "有効期間",
        value: `${formatDateTime(client.validPeriod.from, false, "YYYY/MM/DD HH:mm")} 〜 ${formatDateTime(client.validPeriod.to, false, "YYYY/MM/DD HH:mm")}`,
      },
      {
        name: "登録日",
        value: client.createdAt
          ? formatDateTime(client.createdAt, false, "YYYY/MM/DD HH:mm")
          : "",
      },
      {
        name: "登録者",
        value: client.createdBy ?? "",
      },
    ];
  }, [client]);

  const handleCancel = useCallback(() => {
    navigateToPage(PageRoutes.CLIENTS);
  }, []);

  const handleEdit = useCallback(() => {
    if (!clientId) return;

    navigateToPage(PageRoutes.CLIENT_EDIT.replace(":clientId", clientId));
  }, [clientId]);

  return { clientDetails, handleCancel, handleEdit };
};
